import React, {KeyboardEvent, useLayoutEffect, useRef, ReactNode, ReactElement} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import Button, {ButtonVariant} from '../Buttons/Button/Button';
import {CloseIcon} from '../Icons';
import isElementInViewport from '../../../isElementInViewport';
import KeyCodes from '../../../keyCodes';
import {useSetModalContent} from '../../../RootProvider/ModalContextProvider';
import './BaseModal.scss';

export interface Props {
  children: ReactNode | ((handleClose: () => void) => ReactNode);
  showTopCloseButton?: boolean;
}

function BaseModal({children, showTopCloseButton = true}: Props): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const setModal = useSetModalContent();
  const {formatMessage} = useIntl();

  useLayoutEffect(() => {
    if (ref && ref.current && !isElementInViewport(ref.current)) {
      ref.current.scrollIntoView();
    }
  }, []);

  const handleClose = () => setModal(undefined);

  const handleKeyEvent = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.keyCode === KeyCodes.ESCAPE) {
      handleClose();
    }
  };

  const childrenToRender = typeof children === 'function' ? children(handleClose) : children;

  return (
    <div className="baseModal" id="baseModal">
      <div className="baseModal__wrapper">
        <div
          data-testid="modal.background"
          aria-label={formatMessage({id: 'phoenix.modal.close'})}
          className="baseModal__background"
          onClick={handleClose}
          onKeyDown={handleKeyEvent}
          role="button"
          tabIndex={0}
        />
        <div className="baseModal__container" ref={ref}>
          <div className="baseModal__container__topSection">
            {showTopCloseButton && (
              <Button
                id="closeModal"
                testId="button.closeModal"
                ariaLabel={formatMessage({id: 'phoenix.modal.close'})}
                variant={ButtonVariant.DEFAULT_TEXT}
                onClick={handleClose}
                type="button"
              >
                <CloseIcon />
                <span>
                  <FormattedMessage id="phoenix.modal.close" />
                </span>
              </Button>
            )}
          </div>
          <div className="baseModal__container__content">{childrenToRender}</div>
        </div>
      </div>
    </div>
  );
}

export default BaseModal;
